import React from 'react';
import { Box, styled } from '@mui/material';
import { DateRangePicker, DateRangePickerProps } from 'components/DateRangePicker/DateRangePicker';
import { PageHeader } from 'components/PageHeader/PageHeader';
import { ScrollableContainer } from 'components/ScrollableContainer/ScrollableContainer';
import { WidgetReadingList, ReadingRow } from './WidgetReadingList';

const Column = styled(Box)({
  width: '100%',
  maxWidth: 834,
  marginInline: 'auto',
});

const ChartCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderBottom: `1px solid ${theme.palette.grey[400]}`,
  paddingTop: theme.spacing(2.25),
  paddingBottom: theme.spacing(3.75),
  [theme.breakpoints.up('sm')]: {
    paddingTop: theme.spacing(4.25),
    paddingBottom: theme.spacing(7.5),
  },
}));

export interface WidgetDetailLayoutProps {
  title: string;
  onBack: () => void;
  dateRange: DateRangePickerProps;
  /** Stats between the date range and the chart (e.g. blood pressure ranges, step totals). */
  summary?: React.ReactNode;
  /** The widget's chart (and legend), or its loading / error state. */
  children: React.ReactNode;
  readings: ReadingRow[];
  onDownload?: () => void;
  isDownloading?: boolean;
}

// The page shared by every widget detail screen (Figma: Blood Sugar 8130:18576 tablet, 8139:19503
// desktop, 8077:14523 mobile; Weight, Blood Pressure and Step Count use the same frame). The page
// sits on the ScrollableContainer's light background; only the chart card and the readings rows
// are white, so the "Past Readings" bar reads as a tinted divider between them. Desktop's content
// column is the tablet frame's 834px, centered.
export const WidgetDetailLayout = ({
  title,
  onBack,
  dateRange,
  summary,
  children,
  readings,
  onDownload,
  isDownloading,
}: WidgetDetailLayoutProps) => (
  <ScrollableContainer>
    <PageHeader title={title} isBackVisible onBack={onBack} />
    <Column>
      <ChartCard>
        <DateRangePicker {...dateRange} />
        {summary}
        {children}
      </ChartCard>
      <WidgetReadingList
        readings={readings}
        onDownload={onDownload}
        isDownloading={isDownloading}
      />
    </Column>
  </ScrollableContainer>
);
